import * as React from "react";
import {
  SidebarProvider,
  Sidebar as SidebarRoot,
  SidebarContent,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
} from "app/components/ui/sidebar";
import { cn } from "utils/shadcn";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectUser } from "app/slice/selectors";
import { LayoutDashboard, Package, Code, Settings, Logs, Camera } from "lucide-react";

const routes = [
  {
    group: "Menu",
    label: "Dashboard",
    icon: LayoutDashboard,
    href: "/admin/dashboard",
  },
  {
    group: "Menu",
    label: "Products",
    icon: Package,
    href: "/admin/products",
  },
  {
    group: "Settings",
    label: "Integrations",
    icon: Code,
    href: "/admin/template-setting",
  },
  {
    group: "Behind the scenes",
    label: "Behind the scenes",
    icon: Camera,
    href: "/admin/render-logs",
  },
  {
    group: "Settings",
    label: "Settings",
    icon: Settings,
    href: "/admin/profile-settings",
  },
];

interface SidebarProps {
  className?: string;
}

export function Sidebar({ className }: SidebarProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const user = useSelector(selectUser);

  return (
    <SidebarProvider className={cn(className)}>
      <SidebarRoot
        className={cn(
          "bg-white w-[260px] flex flex-col justify-between border-r border-gray-100",
          "h-screen pt-16" // Full height, but with padding top to clear header
        )}
      >
        <SidebarContent className="px-4 py-6 flex flex-col h-full bg-white">
          {/* Menu Section */}
          <div className="flex-1">
            <SidebarMenu className="gap-2">
              {routes
                .filter((route) => {
                  if (user?.role === "company_user") {
                    return route.label === "Dashboard";
                  }
                  return true;
                })
                .map((route) => {
                  const isActive = location.pathname.startsWith(route.href);
                  const Icon = route.icon;
                  return (
                    <SidebarMenuItem key={route.href}>
                      <SidebarMenuButton
                        data-route={route.href}
                        isActive={isActive}
                        className={cn(
                          "h-[48px] w-full transition-all duration-200 group flex items-center gap-3",
                          isActive ? "" : "hover:bg-[rgba(146,38,213,0.1)] hover:text-[rgb(146,38,213)]"
                        )}
                        style={{
                          borderRadius: "12px",
                          padding: "0px 16px",
                          backgroundColor: isActive
                            ? "rgb(146, 38, 213)"
                            : "transparent",
                          color: isActive
                            ? "white"
                            : "#4B5563",
                          fontSize: "15px",
                          fontWeight: "500"
                        }}
                        onMouseEnter={(e) => {
                          if (!isActive) {
                            e.currentTarget.style.backgroundColor = "rgba(146, 38, 213, 0.1)";
                            e.currentTarget.style.color = "rgb(146, 38, 213)";
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!isActive) {
                            e.currentTarget.style.backgroundColor = "transparent";
                            e.currentTarget.style.color = "#4B5563";
                          }
                        }}
                        onClick={() => navigate(route.href)}
                      >
                        <div className="flex items-center justify-center w-5 h-5">
                          <Icon
                            size={20}
                            color={isActive ? "#FFFFFF" : "currentColor"}
                            style={{
                              color: isActive ? "#FFFFFF" : "inherit"
                            }}
                          />
                        </div>
                        <span>{route.label}</span>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  )
                })}
            </SidebarMenu>
          </div>
        </SidebarContent>
      </SidebarRoot>
    </SidebarProvider>
  );
}
